export interface ISpectraLogicalPlate {
  name: string,
  quadrant: number,
  daughterName?: string,
  data: ISpectraPlateData,
  physicalPlate: ISpectraPhysicalPlate
}

export interface ISpectraPhysicalPlate {
  name: string,
  rowCount: number,
  colCount: number,
  wellCount: number,
  data: ISpectraPlateData,
  logicalPlates: ISpectraLogicalPlate[]
}

export interface ISpectraPlateData {
  readings: { [wavelength: string]: { [position: string]: number } };
  wellCalculations: { [name: string]: { [position: string]: number } };
  calculations: { [name: string]: number };
  passing: boolean;
}

export type ISpectraPlate = ISpectraLogicalPlate | ISpectraPhysicalPlate;

const DILUTION_FACTOR = 6;
const DNA_CONSTANT = 45;

export class SpectraHelper {

  public useDb: boolean = false;
  public physicalPlates: ISpectraPhysicalPlate[] = [];
  public logicalPlates: ISpectraLogicalPlate[] = [];

  private _physicalBlankPlate?: ISpectraPhysicalPlate = undefined;
  private _logicalBlankPlates: ISpectraLogicalPlate[]= [];

  public parse(raw: string): ISpectraLogicalPlate[] {
    //get physical plates from file
    this.physicalPlates = this._fileToPhysicalPlates(raw.trim());

    //convert physical blank plate to logical black plates
    if (this._physicalBlankPlate) {
      this._logicalBlankPlates = this._convertToLogicalPlates(this._physicalBlankPlate);
    }

    //convert physical plates to logical plates (for non-blank plates)
    this.logicalPlates = [];
    for (let physicalPlate of this.physicalPlates) {
      this.logicalPlates = this.logicalPlates.concat(this._convertToLogicalPlates(physicalPlate));
    }

    return this.logicalPlates;
  }

  private _fileToPhysicalPlates(raw: string): ISpectraPhysicalPlate[] {
    let rows = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];

    let lines = raw.replace("\r", "").split("\n");
    let data: string[][] = [];
    for (let i in lines) {
      data[i] = lines[i].split("\t");
    }

    let physicalPlates: ISpectraPhysicalPlate[] = [];

    let numPlates = parseInt(data[0][0].replace("##BLOCKS=", "").trim());
    let plateIdx = 0;
    for (let plateRowAbs = 1; plateRowAbs < data.length; plateRowAbs++) {
      let physicalPlate: ISpectraPhysicalPlate = {
        name: data[plateRowAbs][1],
        rowCount: parseInt(data[plateRowAbs][20]),
        colCount: parseInt(data[plateRowAbs][17]),
        wellCount: parseInt(data[plateRowAbs][18]),
        data: {
          readings: {},
          wellCalculations: {},
          calculations: {},
          passing: false,
        },
        logicalPlates: []
      };

      let readingTypes = data[plateRowAbs][15].trim().split(' ')
      let firstPlateRowAbs = plateRowAbs + 2;

      let readingColStart = 2;
      for (let readingTypeIdx = 0; readingTypeIdx < readingTypes.length; readingTypeIdx++) {
        let readingType = readingTypes[readingTypeIdx];

        physicalPlate.data.readings[readingType] = {};

        if (readingTypeIdx > 0) {
          readingColStart += physicalPlate.colCount + 1;
        }

        for (let rowAbs = firstPlateRowAbs; rowAbs < firstPlateRowAbs + physicalPlate.rowCount; rowAbs++) {
          let plateRow = rowAbs - firstPlateRowAbs;

          for (let colAbs = readingColStart; colAbs < readingColStart + physicalPlate.colCount; colAbs++) {
            let plateCol = colAbs - readingColStart;

            let reading = data[rowAbs][colAbs];

            let rowName = rows[plateRow];
            let columnName = (plateCol + 1 < 10 ? '0' + (plateCol + 1) : (plateCol + 1).toString());
            let wellName = rowName + columnName;

            physicalPlate.data.readings[readingType][wellName] = parseFloat(reading);
          }
        }
      }

      if (physicalPlate.name.toUpperCase().substring(0, 5) === 'BLANK') {
        this._physicalBlankPlate = physicalPlate;
      } else {
        physicalPlates.push(physicalPlate);
      }

      plateRowAbs += physicalPlate.rowCount + 3;
      plateIdx++;

      if (plateIdx > numPlates - 1) {
        break;
      }
    }

    return physicalPlates;
  }

  private _convertToLogicalPlates(physicalPlate: ISpectraPhysicalPlate): ISpectraLogicalPlate[] {
    if (physicalPlate.wellCount === 384) {
      return this._convertPlate384ToLogical(physicalPlate);
    }
    if (physicalPlate.wellCount === 96) {
      return this._convertPlate96ToLogical(physicalPlate);
    }

    //shouldn't get here
    return [];
  }

  private _convertPlate384ToLogical(physicalPlate: ISpectraPhysicalPlate): ISpectraLogicalPlate[] {
    let map: any = { "1": { "A01": "A01", "A03": "A02", "A05": "A03", "A07": "A04", "A09": "A05", "A11": "A06", "A13": "A07", "A15": "A08", "A17": "A09", "A19": "A10", "A21": "A11", "A23": "A12", "C01": "B01", "C03": "B02", "C05": "B03", "C07": "B04", "C09": "B05", "C11": "B06", "C13": "B07", "C15": "B08", "C17": "B09", "C19": "B10", "C21": "B11", "C23": "B12", "E01": "C01", "E03": "C02", "E05": "C03", "E07": "C04", "E09": "C05", "E11": "C06", "E13": "C07", "E15": "C08", "E17": "C09", "E19": "C10", "E21": "C11", "E23": "C12", "G01": "D01", "G03": "D02", "G05": "D03", "G07": "D04", "G09": "D05", "G11": "D06", "G13": "D07", "G15": "D08", "G17": "D09", "G19": "D10", "G21": "D11", "G23": "D12", "I01": "E01", "I03": "E02", "I05": "E03", "I07": "E04", "I09": "E05", "I11": "E06", "I13": "E07", "I15": "E08", "I17": "E09", "I19": "E10", "I21": "E11", "I23": "E12", "K01": "F01", "K03": "F02", "K05": "F03", "K07": "F04", "K09": "F05", "K11": "F06", "K13": "F07", "K15": "F08", "K17": "F09", "K19": "F10", "K21": "F11", "K23": "F12", "M01": "G01", "M03": "G02", "M05": "G03", "M07": "G04", "M09": "G05", "M11": "G06", "M13": "G07", "M15": "G08", "M17": "G09", "M19": "G10", "M21": "G11", "M23": "G12", "O01": "H01", "O03": "H02", "O05": "H03", "O07": "H04", "O09": "H05", "O11": "H06", "O13": "H07", "O15": "H08", "O17": "H09", "O19": "H10", "O21": "H11", "O23": "H12" }, "2": { "A02": "A01", "A04": "A02", "A06": "A03", "A08": "A04", "A10": "A05", "A12": "A06", "A14": "A07", "A16": "A08", "A18": "A09", "A20": "A10", "A22": "A11", "A24": "A12", "C02": "B01", "C04": "B02", "C06": "B03", "C08": "B04", "C10": "B05", "C12": "B06", "C14": "B07", "C16": "B08", "C18": "B09", "C20": "B10", "C22": "B11", "C24": "B12", "E02": "C01", "E04": "C02", "E06": "C03", "E08": "C04", "E10": "C05", "E12": "C06", "E14": "C07", "E16": "C08", "E18": "C09", "E20": "C10", "E22": "C11", "E24": "C12", "G02": "D01", "G04": "D02", "G06": "D03", "G08": "D04", "G10": "D05", "G12": "D06", "G14": "D07", "G16": "D08", "G18": "D09", "G20": "D10", "G22": "D11", "G24": "D12", "I02": "E01", "I04": "E02", "I06": "E03", "I08": "E04", "I10": "E05", "I12": "E06", "I14": "E07", "I16": "E08", "I18": "E09", "I20": "E10", "I22": "E11", "I24": "E12", "K02": "F01", "K04": "F02", "K06": "F03", "K08": "F04", "K10": "F05", "K12": "F06", "K14": "F07", "K16": "F08", "K18": "F09", "K20": "F10", "K22": "F11", "K24": "F12", "M02": "G01", "M04": "G02", "M06": "G03", "M08": "G04", "M10": "G05", "M12": "G06", "M14": "G07", "M16": "G08", "M18": "G09", "M20": "G10", "M22": "G11", "M24": "G12", "O02": "H01", "O04": "H02", "O06": "H03", "O08": "H04", "O10": "H05", "O12": "H06", "O14": "H07", "O16": "H08", "O18": "H09", "O20": "H10", "O22": "H11", "O24": "H12" }, "3": { "B01": "A01", "B03": "A02", "B05": "A03", "B07": "A04", "B09": "A05", "B11": "A06", "B13": "A07", "B15": "A08", "B17": "A09", "B19": "A10", "B21": "A11", "B23": "A12", "D01": "B01", "D03": "B02", "D05": "B03", "D07": "B04", "D09": "B05", "D11": "B06", "D13": "B07", "D15": "B08", "D17": "B09", "D19": "B10", "D21": "B11", "D23": "B12", "F01": "C01", "F03": "C02", "F05": "C03", "F07": "C04", "F09": "C05", "F11": "C06", "F13": "C07", "F15": "C08", "F17": "C09", "F19": "C10", "F21": "C11", "F23": "C12", "H01": "D01", "H03": "D02", "H05": "D03", "H07": "D04", "H09": "D05", "H11": "D06", "H13": "D07", "H15": "D08", "H17": "D09", "H19": "D10", "H21": "D11", "H23": "D12", "J01": "E01", "J03": "E02", "J05": "E03", "J07": "E04", "J09": "E05", "J11": "E06", "J13": "E07", "J15": "E08", "J17": "E09", "J19": "E10", "J21": "E11", "J23": "E12", "L01": "F01", "L03": "F02", "L05": "F03", "L07": "F04", "L09": "F05", "L11": "F06", "L13": "F07", "L15": "F08", "L17": "F09", "L19": "F10", "L21": "F11", "L23": "F12", "N01": "G01", "N03": "G02", "N05": "G03", "N07": "G04", "N09": "G05", "N11": "G06", "N13": "G07", "N15": "G08", "N17": "G09", "N19": "G10", "N21": "G11", "N23": "G12", "P01": "H01", "P03": "H02", "P05": "H03", "P07": "H04", "P09": "H05", "P11": "H06", "P13": "H07", "P15": "H08", "P17": "H09", "P19": "H10", "P21": "H11", "P23": "H12" }, "4": { "B02": "A01", "B04": "A02", "B06": "A03", "B08": "A04", "B10": "A05", "B12": "A06", "B14": "A07", "B16": "A08", "B18": "A09", "B20": "A10", "B22": "A11", "B24": "A12", "D02": "B01", "D04": "B02", "D06": "B03", "D08": "B04", "D10": "B05", "D12": "B06", "D14": "B07", "D16": "B08", "D18": "B09", "D20": "B10", "D22": "B11", "D24": "B12", "F02": "C01", "F04": "C02", "F06": "C03", "F08": "C04", "F10": "C05", "F12": "C06", "F14": "C07", "F16": "C08", "F18": "C09", "F20": "C10", "F22": "C11", "F24": "C12", "H02": "D01", "H04": "D02", "H06": "D03", "H08": "D04", "H10": "D05", "H12": "D06", "H14": "D07", "H16": "D08", "H18": "D09", "H20": "D10", "H22": "D11", "H24": "D12", "J02": "E01", "J04": "E02", "J06": "E03", "J08": "E04", "J10": "E05", "J12": "E06", "J14": "E07", "J16": "E08", "J18": "E09", "J20": "E10", "J22": "E11", "J24": "E12", "L02": "F01", "L04": "F02", "L06": "F03", "L08": "F04", "L10": "F05", "L12": "F06", "L14": "F07", "L16": "F08", "L18": "F09", "L20": "F10", "L22": "F11", "L24": "F12", "N02": "G01", "N04": "G02", "N06": "G03", "N08": "G04", "N10": "G05", "N12": "G06", "N14": "G07", "N16": "G08", "N18": "G09", "N20": "G10", "N22": "G11", "N24": "G12", "P02": "H01", "P04": "H02", "P06": "H03", "P08": "H04", "P10": "H05", "P12": "H06", "P14": "H07", "P16": "H08", "P18": "H09", "P20": "H10", "P22": "H11", "P24": "H12" } };

    let results: ISpectraLogicalPlate[] = [];

    for (let quadrant = 1; quadrant <= 4; quadrant++) {
      let logicalPlate: ISpectraLogicalPlate = {
        name: physicalPlate.name + " Q" + quadrant,
        quadrant: quadrant,
        data: {
          calculations: {},
          wellCalculations: {},
          readings: {},
          passing: false,
        },
        physicalPlate: physicalPlate
      };

      for (let wavelength in physicalPlate.data.readings) {
        logicalPlate.data.readings[wavelength] = {};

        for (let position in map[quadrant]) {
          let destPosition = map[quadrant.toString()][position];
          logicalPlate.data.readings[wavelength][destPosition] = physicalPlate.data.readings[wavelength][position];
        }
      }

      if (physicalPlate === this._physicalBlankPlate) {
        this._performBlankPlateCalculations(logicalPlate);
      }
      else if (this._logicalBlankPlates[quadrant - 1]) {
        this._performPlateCalculations(logicalPlate, this._logicalBlankPlates[quadrant - 1])
      }

      results.push(logicalPlate);
    }

    physicalPlate.logicalPlates = results;
    return results;
  }

  private _convertPlate96ToLogical(physicalPlate: ISpectraPhysicalPlate): ISpectraLogicalPlate[] {
    let results: ISpectraLogicalPlate[] = [];
    let logicalPlate: ISpectraLogicalPlate = {
      name: physicalPlate.name,
      quadrant: 1,
      data: {
        calculations: {},
        wellCalculations: {},
        readings: physicalPlate.data.readings,
        passing: false,
      },
      physicalPlate: physicalPlate
    };
    
    if (physicalPlate === this._physicalBlankPlate) {
      this._performBlankPlateCalculations(logicalPlate);
    }
    else if (this._logicalBlankPlates[0]) {
      this._performPlateCalculations(logicalPlate, this._logicalBlankPlates[0])
    }

    results.push(logicalPlate);
    physicalPlate.logicalPlates = results;
 
    return results;
  }

  private _performBlankPlateCalculations(spectraPlate: ISpectraPlate) {
    let corrected230: { [well: string]: number } = {};
    let corrected260: { [well: string]: number } = {};
    let corrected280: { [well: string]: number } = {};

    let corrected230values = [];
    for (let well in spectraPlate.data.readings['230']) {
      corrected230[well] = spectraPlate.data.readings['230'][well] - spectraPlate.data.readings['320'][well];
      corrected230values.push(corrected230[well]);
    }

    let corrected260values = [];
    for (let well in spectraPlate.data.readings['260']) {
      corrected260[well] = spectraPlate.data.readings['260'][well] - spectraPlate.data.readings['320'][well];
      corrected260values.push(corrected260[well]);
    }

    let corrected280values = [];
    for (let well in spectraPlate.data.readings['280']) {
      corrected280[well] = spectraPlate.data.readings['280'][well] - spectraPlate.data.readings['320'][well];
      corrected280values.push(corrected280[well]);
    }

    spectraPlate.data.wellCalculations['corrected230'] = corrected230;
    spectraPlate.data.wellCalculations['corrected260'] = corrected260;
    spectraPlate.data.wellCalculations['corrected280'] = corrected280;

    spectraPlate.data.calculations['corrected230avg'] = this._getAverage(corrected230values);
    spectraPlate.data.calculations['corrected260avg'] = this._getAverage(corrected260values);
    spectraPlate.data.calculations['corrected280avg'] = this._getAverage(corrected280values);

    spectraPlate.data.calculations['corrected230stddev'] = this._getStdDev(corrected230values);
    spectraPlate.data.calculations['corrected260stddev'] = this._getStdDev(corrected260values);
    spectraPlate.data.calculations['corrected280stddev'] = this._getStdDev(corrected280values);
  }

  private _performPlateCalculations(plate: ISpectraPlate, blankPlate: ISpectraPlate) {
    let corrected230: { [well: string]: number } = {};
    let corrected260: { [well: string]: number } = {};
    let corrected280: { [well: string]: number } = {};

    let corrected230values = [];
    for (let well in plate.data.readings['230']) {
      corrected230[well] = plate.data.readings['230'][well] - plate.data.readings['320'][well];
      corrected230values.push(corrected230[well]);
    }

    let corrected260values = [];
    for (let well in plate.data.readings['260']) {
      corrected260[well] = plate.data.readings['260'][well] - plate.data.readings['320'][well];
      corrected260values.push(corrected260[well]);
    }

    let corrected280values = [];
    for (let well in plate.data.readings['280']) {
      corrected280[well] = plate.data.readings['280'][well] - plate.data.readings['320'][well];
      corrected280values.push(corrected280[well]);
    }

    plate.data.wellCalculations['corrected230'] = corrected230;
    plate.data.wellCalculations['corrected260'] = corrected260;
    plate.data.wellCalculations['corrected280'] = corrected280;

    let final230: { [well: string]: number } = {};
    let final260: { [well: string]: number } = {};
    let final280: { [well: string]: number } = {};

    for (let well in plate.data.readings['230']) {
      final230[well] = plate.data.wellCalculations['corrected230'][well] - blankPlate.data.calculations['corrected230avg'];
    }

    for (let well in plate.data.readings['280']) {
      final280[well] = plate.data.wellCalculations['corrected280'][well] - blankPlate.data.calculations['corrected280avg'];
    }

    let nonBlankWellCalculations: {
      '260o230': number[],
      '260o280': number[],
      'ngoul': number[]
    } = {
        '260o230': [],
        '260o280': [],
        'ngoul': []
    };
    plate.data.wellCalculations['260o230'] = {};
    plate.data.wellCalculations['260o280'] = {};
    plate.data.wellCalculations['ngoul'] = {};

    for (let well in plate.data.readings['260']) {
      final260[well] = plate.data.wellCalculations['corrected260'][well] - blankPlate.data.calculations['corrected260avg'];
      plate.data.wellCalculations['260o230'][well] = final260[well] / final230[well];
      plate.data.wellCalculations['260o280'][well] = final260[well] / final280[well];
      plate.data.wellCalculations['ngoul'][well] = final260[well] * DILUTION_FACTOR * DNA_CONSTANT;

      if (plate.name.indexOf('OT') === 0 && (well === 'A01' || well === 'A07')) {
        //if plate name begins with OT, skip A01 and A07
        continue;
      }
      nonBlankWellCalculations['260o230'].push(plate.data.wellCalculations['260o230'][well]);
      nonBlankWellCalculations['260o280'].push(plate.data.wellCalculations['260o280'][well]);
      nonBlankWellCalculations['ngoul'].push(plate.data.wellCalculations['ngoul'][well]);
    }

    plate.data.wellCalculations['final230'] = final230;
    plate.data.wellCalculations['final260'] = final260;
    plate.data.wellCalculations['final280'] = final280;

    plate.data.calculations['avg_ngoul'] = this._getAverage(nonBlankWellCalculations['ngoul']);
    plate.data.calculations['stddev_ngoul'] = this._getStdDev(nonBlankWellCalculations['ngoul']);
    plate.data.calculations['avg_260o280'] = this._getAverage(nonBlankWellCalculations['260o280']);
    plate.data.calculations['stddev_260o280'] = this._getStdDev(nonBlankWellCalculations['260o280']);

    plate.data.calculations['A01'] = plate.data.wellCalculations['ngoul']['A01'];
    plate.data.calculations['E01'] = plate.data.wellCalculations['ngoul']['E01'];
    plate.data.calculations['D12'] = plate.data.wellCalculations['ngoul']['D12'];
    plate.data.calculations['H12'] = plate.data.wellCalculations['ngoul']['H12'];

    this._performMyDNAPlateCalculations(plate, blankPlate);

    if (plate.data.calculations['A01'] < 1.5 && plate.data.calculations['E01'] < 1.5 && (plate.data.calculations['D12'] > 5 || plate.data.calculations['H12'] > 5)) {
      plate.data.passing = true;
    } else {
      plate.data.passing = false;
    }
  }

  private _performMyDNAPlateCalculations(plate: ISpectraPlate, blankPlate: ISpectraPlate) {
    plate.data.wellCalculations['mydna_260'] = {};
    
    for (let well in plate.data.readings['260']) {
      plate.data.wellCalculations['mydna_260'][well] = ((plate.data.readings['260'][well] - blankPlate.data.readings['260'][well]) - (plate.data.readings['320'][well] - blankPlate.data.readings['320'][well])) * DILUTION_FACTOR * DNA_CONSTANT;
    }
  }
  
  private _getAverage(values: number[]): number {
    var sum = values.reduce(function (sum, value) {
      return sum + value;
    }, 0);

    return sum / values.length;
  }

  private _getStdDev(values: number[]): number {
    var avg = this._getAverage(values);
    var sqrDiffs = values.map(function (value) {
      var diff = value - avg;
      var sqrDiff = diff * diff;
      return sqrDiff;
    });

    var sum = sqrDiffs.reduce(function (sum, value) {
      return sum + value;
    }, 0);
    var avgSqrDiff = sum / (sqrDiffs.length - 1);

    var stdDev = Math.sqrt(avgSqrDiff);
    return stdDev;
  }
}